<template>
    <v-dialog v-model="showPopup" persistent width="978">
        <v-card>
            <v-card-title>
                <h2 v-if="!messageSent">{{ lang.get('Ask your teacher') }}</h2>
                <h2 v-else>{{ lang.get('Thank you') }}</h2>
            </v-card-title>
            <v-card-text>
                <template v-if="!messageSent">
                    <div v-if="teachers.length == 0" class="mb-3">
                        {{ lang.get('Your teacher is not on Giglets, ask your class teacher in person.') }}
                    </div>
                    <template v-else-if="teachers.length > 1">
                        <v-select
                            v-model="teacherSelected"
                            :label="lang.get('To')"
                            :items="teachers"
                            item-title="name"
                            item-value="uid"
                        ></v-select>
                    </template>
                    <template v-else>
                        <div class="mb-3">
                            {{ lang.get('To') }}: {{ teachers.find((t) => t.uid == teacherSelected)?.name || '' }}
                        </div>
                    </template>
                    <v-text-field
                        v-model="context"
                        :label="lang.get('What were you doing')"
                        name="context"
                    ></v-text-field>
                    <v-textarea
                        v-model="question"
                        :label="lang.get('Your question for the teacher')"
                        name="question"
                    ></v-textarea>
                    <v-card-actions>
                        <div v-if="error" class="d-flex">
                            <v-chip color="error">Error: {{ error }}</v-chip>
                        </div>
                        <v-spacer />
                        <a class="btn mx-5" @click="close">{{ lang.get('Cancel') }}</a>
                        <a class="btn primary" @click="sendMessage">{{ lang.get('Send') }}</a>
                    </v-card-actions>
                </template>
                <template v-else>
                    <p class="mb-3">
                        {{
                            lang.get(
                                'Thank you, the message has been sent to %s.',
                                teachers.find((t) => t.uid == teacherSelected)?.name || '',
                            )
                        }}
                    </p>
                    <v-card-actions>
                        <v-spacer />
                        <a id="close" class="btn primary px-5" @click="close">{{ lang.get('Ok') }}</a>
                    </v-card-actions>
                </template>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>
<script lang="ts" setup>
import { storeToRefs } from 'pinia';

defineExpose({
    show,
});

const lang = useLang();
const store = usePupilStore();
store.fetchTeachers();
store.fetchBook();
store.fetchSchool();
store.fetchPupil();
const { teachers, book, pupil, school } = storeToRefs(store);

const showPopup = ref(false);
const messageSent = ref(false);
const teacherSelected = ref<string>();
const question = ref('');
const context = ref('');
watchEffect(() => {
    context.value = lang.get('Reading') + ' ' + book.value?.title;
});
const error = ref<string | undefined>();

watchEffect(() => {
    if (teachers.value.length > 0) {
        teacherSelected.value = teachers.value[0].uid;
    }
});

function show(helpWords?: string) {
    if (helpWords) {
        question.value = lang.get("I'd like help with the words") + ': "' + helpWords + '"';
    } else {
        question.value = '';
    }
    showPopup.value = true;
}

function close() {
    question.value = '';
    messageSent.value = false;
    error.value = undefined;
    showPopup.value = false;
}

watchEffect(() => {
    if (school.value?.ask_teacher) {
        (window as any).window.askTeacher = function (helpWords: string) {
            show(helpWords);
        };
    } else {
        (window as any).window.askTeacher = undefined;
    }
});

async function sendMessage() {
    if (!question.value) {
        error.value = 'Please fill in the question.';
        return;
    }
    if (!teacherSelected.value) {
        error.value = 'Please select your teacher.';
        return;
    }
    try {
        error.value = undefined;
        await store.createMessage({
            teacher: { uid: teacherSelected.value },
            pupil: { uid: pupil.value!.uid },
            message: question.value,
            context: context.value,
            to_teacher: true,
        });
        messageSent.value = true;
    } catch (e) {
        error.value = 'We could not send the message, please try again later.';
        console.error(e);
    }
}
</script>
<style lang="scss" scoped>
.error {
    color: red;
    margin-bottom: 1rem;
}
</style>
